import React from 'react';
import { Flex, TextV2, Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { useHotelUserRating } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelUserRating';
import { TravelMapBlockType } from '@apps/admin/routes/Planning/routes/Accommodations/Accommodations.types';
import { AccommodationCombinedType } from '@apps/guest/packages/layout-engine/widgets/Custom/components/TravelMap/components/Accommodations/Accommodation.types';
import { useTransformRatingToCategory } from '@apps/admin/routes/Planning/components/HotelsListV2/hooks/useTransformRatingToCategory';

export interface HotelArgs {
  type?: TravelMapBlockType | AccommodationCombinedType;
  id?: string | null;
  itemId?: string;
  joyPlaceId?: string | null;
  zhId?: string | null;
  rating?: number;
  ratingCount?: number;
}

interface HotelRatingWithCountProps {
  hotel: HotelArgs;
  variant?: 'small' | 'large' | 'mobileSmall';
}

export const HotelRatingWithCount: React.FC<HotelRatingWithCountProps> = ({ hotel, variant }) => {
  const { t } = useTranslation('planningAccommodations');
  const { hotelItem } = t('browse');
  const isSmall = variant === 'small';
  const isMobileSmall = variant === 'mobileSmall';

  const { reviewCount, validatedRating, ratingCategory, loading } = useHotelUserRating({
    hotel,
    context: 'admin accommodation details pane user rating',
    skip: !!hotel?.rating
  });

  const hotelRatingCategory = useTransformRatingToCategory(hotel?.rating);
  const ratingCategoryString = hotelRatingCategory || ratingCategory;

  const getFlexDirection = () => {
    if (isMobileSmall) {
      return 'column-reverse';
    }
    if (isSmall) {
      return 'row-reverse';
    }
    return 'row';
  };

  if (loading) {
    return (
      <Flex gap={5} width="fit-content" flexDirection={getFlexDirection()} alignItems={isMobileSmall ? 'flex-end' : 'center'}>
        <Flex flexDirection="column" gap={1}>
          <Skeleton
            placeholder={
              <Box width={pxToRem(80)} height={pxToRem(16)} backgroundColor="mono3">
                &nbsp;
              </Box>
            }
          />
          <Skeleton
            placeholder={
              <Box width={pxToRem(60)} height={pxToRem(14)} backgroundColor="mono3">
                &nbsp;
              </Box>
            }
          />
        </Flex>
        <Skeleton
          placeholder={
            <Box width={pxToRem(40)} height={pxToRem(40)} backgroundColor="mono3" borderRadius={pxToRem(6)}>
              &nbsp;
            </Box>
          }
        />
      </Flex>
    );
  }

  if (!reviewCount && !validatedRating && !hotel.rating && !hotel.ratingCount) {
    return null;
  }

  return (
    <Flex gap={5} width="fit-content" flexDirection={getFlexDirection()} alignItems={isMobileSmall ? 'flex-end' : 'center'}>
      <Flex flexDirection="column" gap={1} textAlign={isSmall ? 'start' : 'end'}>
        <TextV2 typographyVariant="button1" color="mono14" whiteSpace="nowrap">
          {ratingCategoryString}
        </TextV2>
        {(hotel?.ratingCount || reviewCount) && (
          <TextV2 typographyVariant="label2" color="mono10" whiteSpace="nowrap">
            {hotel?.ratingCount ? hotelItem.reviews({ reviews: hotel.ratingCount.toString() }) : hotelItem.reviews({ reviews: (reviewCount || 0).toString() })}
          </TextV2>
        )}
      </Flex>
      <Flex
        typographyVariant="button1"
        backgroundColor="newUITest1"
        borderRadius={pxToRem(6)}
        color="white"
        alignItems="center"
        justifyContent="center"
        height={pxToRem(40)}
        width={pxToRem(40)}
      >
        {hotel?.rating || validatedRating}
      </Flex>
    </Flex>
  );
};
